/**
 * Centralized React Query key factory.
 *
 * Every queryKey / mutationKey in the app should be defined here so cache
 * dependencies are visible in one place without reading every useQuery call.
 */

export const queryKeys = {
  // AUTH / USER
  auth: {
    userInfo: () => ['user-info'] as const,
    platformBanner: () => ['platform-banner'] as const,
    companyTypes: () => ['companyTypes'] as const,
  },

  // SEARCH
  search: {
    /** Filter sidebar metadata shared across Search, home DistributionGraph, and rating comparison charts */
    filterMeta: () => ['projects-filter-meta'] as const,
    projects: (...args: [filters: Record<string, unknown>]) =>
      ['get-all-projects', ...args] as const,
    /** Prefix for invalidating every `projects(...)` filter variant. */
    projectsAll: () => ['get-all-projects'] as const,
    stats: (...args: [filters: Record<string, unknown>]) => ['project-stats', ...args] as const,
  },

  // PROJECT
  project: {
    detail: (...args: [creditingPeriodId: string]) => ['project-detail', ...args] as const,
    sellerOffers: (...args: [uuid: string]) => ['project-seller-offers', ...args] as const,
    sellerOffersSummary: (...args: [uuid: string]) => ['seller-offers-summary', ...args] as const,
    esrSummary: (...args: [id: string]) => ['esr_summary', ...args] as const,
    esrDetail: (...args: [id: string]) => ['esr_detail_summary', ...args] as const,
    esrComparison: (...args: [id: string, type: string]) =>
      ['esr_project_comparison', ...args] as const,
    ghgRating: (...args: [cpNo: string]) => ['ghg-rating', ...args] as const,
    sdgRating: (...args: [creditingPeriodId: string]) =>
      ['sdg-rating-detail-data', ...args] as const,
    annualCreditSummary: (...args: [standard: string, id: string]) =>
      ['project-annual-credit-summary', ...args] as const,
    creditAvailability: (creditingPeriodId: string) =>
      [`project-crediting-${creditingPeriodId}`] as const,
    ratingComparison: (
      ...args: [projectId: string, creditingPeriodId: string, standard: string, filterType: string]
    ) => ['rating-comparison', ...args] as const,
    priceSummary: (...args: [creditingPeriodId: string]) => ['pricing-summary', ...args] as const,
    priceRangeByVintage: (...args: [creditingPeriodId: string]) =>
      ['price-range-by-vintage', ...args] as const,
    tradePriceDistribution: (...args: [creditingPeriodId: string, distributionType: string]) =>
      ['trade-price-distribution', ...args] as const,
    estimatedTradePrice: (...args: [creditingPeriodId: string]) =>
      ['estimated-trade-price', ...args] as const,
  },

  // RETIREE INSIGHTS
  retiree: {
    filterMeta: () => ['retiree-filter-meta'] as const,
    list: (...args: [filters: Record<string, unknown>]) => ['get-all-retiree', ...args] as const,
    aggregate: (...args: [name?: string]) => ['retiree-aggregate-data', ...args] as const,
    marketShare: (...args: [name?: string]) => ['retiree-market-share-data', ...args] as const,
    projectPortfolio: (...args: [name?: string]) =>
      ['retiree-project-portfolio-data', ...args] as const,
    analytics: (...args: [standard: string | null, projectId: string | null]) =>
      ['retiree-analytics', ...args] as const,
    breakdownTimeseries: (
      ...args: [
        name: string | undefined,
        breakdownType: string,
        chartType: string,
        includeNonRated: boolean,
      ]
    ) => ['retiree-breakdown-stats-timeseries-data', ...args] as const,
    breakdownPieChart: (...args: [name: string, breakdownType: string, includeNonRated: boolean]) =>
      ['retiree-breakdown-stats-pie-chart-data', ...args] as const,
  },

  // INSIGHTS HUB
  insightsHub: {
    article: (...args: [slug: string]) => ['insights-hub', ...args] as const,
    byCategory: (...args: [category: string | Array<string> | null]) =>
      ['insights-hub', ...args] as const,
    byProjectType: (...args: [type: string]) => ['insights-hub', ...args, 'pinned-sort'] as const,
    researchUpdate: (...args: [date: string]) => ['research-update', ...args] as const,
    allResearchUpdates: () => ['research-updates'] as const,
    pinnedPost: () => ['pinned-post-aggregate-data'] as const,
  },

  // HOME DASHBOARD
  home: {
    distributionGraph: (type: 'ghg' | 'sdg') => [`${type}-graph`] as const,
    ratings: (viewKey: string) => [`dashboard-ratings-${viewKey}`] as const,
  },

  // NAVBAR
  navbar: {
    followedProjectsCount: () => ['followed-projects-count'] as const,
  },

  // WHERE TO BUY
  whereToBuy: {
    projects: () => ['where-to-buy-projects'] as const,
  },

  // SUPPLYLINK
  supplyLink: {
    filterMeta: () => ['supplylink-filter-meta'] as const,
    list: (...args: [filters: Record<string, unknown>]) => ['supply-link-list', ...args] as const,
  },

  demo: {
    resolve: (...args: [token: string]) => ['demo', 'resolve', ...args] as const,
    me: () => ['demo', 'me'] as const,
  },

  // COMPASS
  compass: {
    updates: (...args: [page: number, pageSize: number]) => ['compass-updates', ...args] as const,
    module: (...args: [moduleId: string]) => ['compass-module', ...args] as const,
    ratingGraph: (...args: [type: 'ghg' | 'sdg']) => ['ratingGraph', ...args] as const,
    methodologyRisk: (...args: [type: string]) => ['methodology-risk', ...args] as const,
    scopeMethodologyRisk: () => ['scope', 'methodology-risk'] as const,
  },

  // MY PLAN
  // Counters (`benefits`) live on `/user/me` itself (queryKeys.auth.userInfo).
  // Mutations that change counters invalidate `userInfo`. The keys below
  // cover the My-Plan routes that don't piggy-back on `/user/me`.
  plan: {
    deepDivePicker: (...args: [page: number, search: string]) =>
      ['deep-dive-picker', ...args] as const,
    /** Prefix for invalidating every `deepDivePicker(...)` variant. */
    deepDivePickerAll: () => ['deep-dive-picker'] as const,
    deepDiveRequests: () => ['deep-dive-requests'] as const,
    fastpassUnratedSearch: (...args: [search: string]) =>
      ['fastpass-unrated-search', ...args] as const,
    seats: () => ['plan-seats'] as const,
    // Canonical advisory-requests list. The dashboard (mode=dashboard, 5-cap),
    // /my-advisory-requests page (mode=full), and My Plan's deep-dive section
    // (mode=full + type=deep_dive) all use the same key prefix so any
    // counter-debiting mutation can invalidate all consumers in one call.
    advisoryRequests: (
      ...args: [
        params: { mode?: string; type?: string; status?: string; search?: string; page?: number },
      ]
    ) => ['advisory-requests', ...args] as const,
    advisoryRequestsAll: () => ['advisory-requests'] as const,
  },

  // RATINGS QUEUE (Upcoming ratings tab)
  // A vote/fastpass mutation invalidates `listAll()` (re-rank + confidential) plus
  // `auth.userInfo()` (the votes/fastpass counters live on `/user/me`).
  queue: {
    list: (...args: [filters: Record<string, unknown>]) => ['ratings-queue', ...args] as const,
    /** Prefix for invalidating every `list(...)` filter variant. */
    listAll: () => ['ratings-queue'] as const,
    /** Single-project vote tally for the exec-summary request-a-rating modal. */
    voteStatus: (id: string) => ['ratings-queue', 'vote-status', id] as const,
  },
};
